import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import DepartureTopStrip from '../../components/ui/DepartureTopStrip';
import { db } from '../../firebase';

const relativeTime = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const trackingChip = (status) => {
  const map = {
    active: { label: 'ACTIVE', color: 'var(--c-success)', bg: 'rgba(22,163,74,.10)' },
    paused: { label: 'PAUSED', color: 'var(--c-warning)', bg: 'rgba(217,119,6,.10)' },
    denied: { label: 'DENIED', color: 'var(--c-danger)', bg: 'rgba(220,38,38,.10)' },
  };
  const cfg = map[status] || { label: 'UNKNOWN', color: 'var(--c-ink-3)', bg: 'rgba(107,114,128,.10)' };
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      letterSpacing: '.12em',
      fontWeight: 600,
      color: cfg.color,
      background: cfg.bg,
      padding: '3px 8px',
      display: 'inline-block',
    }}>
      {cfg.label}
    </span>
  );
};

// Fraud risk chip — reads from supervisorFraudSummary written by the
// fraudAggregation Cloud Function (30-day rolling window).
const fraudChip = (summary) => {
  if (!summary) {
    return (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--c-ink-3)' }}>—</span>
    );
  }
  const tier = summary.riskTier || 'unknown';
  const map = {
    high: { label: 'HIGH RISK', color: 'var(--c-danger)', bg: 'rgba(220,38,38,.10)' },
    medium: { label: 'MEDIUM', color: 'var(--c-warning)', bg: 'rgba(217,119,6,.10)' },
    low: { label: 'LOW', color: 'var(--c-success)', bg: 'rgba(22,163,74,.10)' },
    unknown: { label: 'NO DATA', color: 'var(--c-ink-3)', bg: 'rgba(107,114,128,.10)' },
  };
  const cfg = map[tier] || map.unknown;
  const silent = summary?.totals?.silentDuplicates ?? 0;
  const flaggedPct = summary?.rates?.flaggedPct ?? 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        title={`${summary?.totals?.submissions ?? 0} submissions • ${silent} silent duplicates • ${flaggedPct}% flagged`}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '.12em',
          fontWeight: 600,
          color: cfg.color,
          background: cfg.bg,
          padding: '3px 8px',
        }}
      >
        {cfg.label}
      </span>
      {silent > 0 && (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-3)', fontVariantNumeric: 'tabular-nums slashed-zero' }}>
          {silent}× 👻
        </span>
      )}
    </span>
  );
};

const BatteryDisplay = ({ pct }) => {
  if (pct == null || pct === '') return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--c-ink-3)' }}>—</span>;
  const raw = Number(pct);
  const n = raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
  let Icon;
  let color;
  if (n >= 80) { Icon = BatteryFullIcon; color = 'var(--c-success)'; }
  else if (n >= 40) { Icon = Battery60Icon; color = 'var(--c-warning)'; }
  else if (n >= 15) { Icon = Battery20Icon; color = 'var(--c-danger)'; }
  else { Icon = BatteryAlertIcon; color = 'var(--c-danger)'; }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'var(--font-mono)', fontSize: 12, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)' }}>
      <Icon style={{ fontSize: 16, color }} />
      {n}%
    </span>
  );
};

const cellStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '.08em',
  color: 'var(--c-ink-2)',
  borderBottom: '1px solid var(--c-line-soft)',
  padding: '10px 14px',
};

const headCellStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.16em',
  fontWeight: 600,
  color: 'var(--c-ink-3)',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--c-line-hard)',
  padding: '10px 14px',
  background: 'var(--c-bg)',
};

const SupervisorsList = () => {
  const [supervisors, setSupervisors] = useState([]);
  const [fraudSummaries, setFraudSummaries] = useState({}); // ixora_user_id → summary
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'supervisors'), (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const aid = a.ixora_user_id || '';
        const bid = b.ixora_user_id || '';
        return aid.localeCompare(bid, undefined, { numeric: true });
      });
      setSupervisors(docs);
      setLoading(false);
    }, (err) => {
      console.error('SupervisorsList onSnapshot error:', err);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Subscribe to supervisorFraudSummary written by fraudAggregation CF.
  // Cheap — one doc per supervisor, refreshed on submissions + nightly.
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'supervisorFraudSummary'), (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.ixora_user_id) map[data.ixora_user_id] = data;
      });
      setFraudSummaries(map);
    }, (err) => {
      console.error('SupervisorsList fraudSummary subscription error:', err);
    });
    return unsub;
  }, []);

  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">SUPERVISOR TRACKING</div>
        <div className="db-ribbon-actions">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
            {loading ? 'LOADING…' : `${supervisors.length} SUPERVISORS`}
          </span>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)', padding: 'var(--sp-8) 0' }}>
            <div className="spinner" /> LOADING…
          </div>
        ) : (
          <div style={{ border: '1px solid var(--c-line-soft)', background: 'var(--c-bg)', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  {['Name', 'IxoraOne ID', 'Status', 'Fraud (30d)', 'Last Seen', 'Battery', 'Current Site', ''].map((h) => (
                    <TableCell key={h} sx={headCellStyle}>{h.toUpperCase()}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ ...cellStyle, textAlign: 'center', padding: 'var(--sp-8) 0', color: 'var(--c-ink-3)' }}>
                      NO SUPERVISORS FOUND
                    </TableCell>
                  </TableRow>
                ) : (
                  supervisors.map((s) => (
                    <TableRow
                      key={s.id}
                      sx={{ '&:hover': { background: 'var(--c-bg-2)' }, cursor: 'default' }}
                    >
                      <TableCell sx={{ ...cellStyle, color: 'var(--c-ink)', fontFamily: "'Geist', sans-serif", fontSize: 13, fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={s.display_name || undefined}
                      >
                        {s.display_name || '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, fontVariantNumeric: 'tabular-nums slashed-zero' }}>
                        {s.ixora_user_id || '—'}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {trackingChip(s.tracking || '')}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {fraudChip(fraudSummaries[s.ixora_user_id])}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, fontVariantNumeric: 'tabular-nums slashed-zero' }}>
                        {relativeTime(s.lastSeenAt)}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <BatteryDisplay pct={s.currentBattery} />
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, fontVariantNumeric: 'tabular-nums slashed-zero', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={s.currentSiteId || undefined}
                      >
                        {s.currentSiteId || '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, padding: '10px 14px' }}>
                        <button
                          onClick={() => navigate(`/supervisors/${s.id}`)}
                          className="db-pill"
                          style={{ fontSize: 10, padding: '4px 10px', letterSpacing: '.10em', cursor: 'pointer' }}
                        >
                          VIEW MAP
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
};

export default SupervisorsList;
